import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdZoomIn, MdZoomOut } from "react-icons/md";
import { TbZoomReset } from "react-icons/tb";
import { Document, Page } from "react-pdf";
import { IconButton } from "@mui/material";
import { colors } from "@zapalacorp/tokens";

import {
  PdfViewerWrapper,
  PdfViewerLoading,
  PdfViewerTitle,
  PdfViewerFooter,
  PdfLoadingWrapper,
  PdfPagesStack,
} from "./styles";

/** Horizontal padding from PdfViewerWrapper (48px × 2). */
const WRAPPER_HORIZONTAL_PADDING = 96;

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;

interface PDFViewerProps {
  file: string | File | null;
  filename?: string;
  height?: number | string;
  mt?: number;
  /** Caps width used before/while ResizeObserver measures (e.g. narrow MUI Drawer maxWidth). */
  maxContentWidth?: number;
}

export function PDFViewer({ file, filename, height, mt, maxContentWidth }: PDFViewerProps) {
  const { t: translate } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState<number>(0);
  /** 1 = fit page width to container; lower zooms out, higher zooms in. */
  const [zoom, setZoom] = useState<number>(1);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let rafId = 0;

    // Defer reads + setState to the next frame so we don't trigger
    // "ResizeObserver loop completed with undelivered notifications" when
    // react-pdf changes layout in the same cycle as the observer.
    const scheduleMeasure = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const w = el.clientWidth;
        if (w <= 0) return;
        setContainerWidth((prev) => (prev === w ? prev : w));
      });
    };

    scheduleMeasure();
    const ro = new ResizeObserver(scheduleMeasure);
    ro.observe(el);
    window.addEventListener("resize", scheduleMeasure);
    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, []);

  useEffect(() => {
    setZoom(1);
    setNumPages(0);
  }, [file]);

  const zoomIn = () => setZoom((z) => Math.min(z * 1.2, ZOOM_MAX));
  const zoomOut = () => setZoom((z) => Math.max(z / 1.2, ZOOM_MIN));
  const resetZoom = () => setZoom(1);

  const measuredWidth =
    containerWidth > 0
      ? maxContentWidth != null
        ? Math.min(containerWidth, maxContentWidth)
        : containerWidth
      : maxContentWidth ?? 720;
  const baseFitWidth = Math.max(240, measuredWidth - WRAPPER_HORIZONTAL_PADDING);
  const pageRenderWidth = Math.round(baseFitWidth * zoom);

  function onDocumentLoad({ numPages }: { numPages: number }) {
    setNumPages(numPages);
  }

  return (
    <div>
      <PdfViewerWrapper
        ref={containerRef}
        className="pdf-document-container"
        height={height}
        marginTop={mt}
      >
        <Document
          file={file}
          onLoadSuccess={onDocumentLoad}
          loading={
            <PdfLoadingWrapper>
              <PdfViewerTitle>{translate("uploading")}</PdfViewerTitle>
              <PdfViewerLoading>
                <span>{filename || ""}</span>
              </PdfViewerLoading>
            </PdfLoadingWrapper>
          }
        >
          {numPages > 0 && (
            <PdfPagesStack>
              {Array.from({ length: numPages }, (_, i) => (
                <Page
                  key={`pdf-page-${i + 1}`}
                  pageNumber={i + 1}
                  width={pageRenderWidth}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />
              ))}
            </PdfPagesStack>
          )}
        </Document>
      </PdfViewerWrapper>
      <PdfViewerFooter>
        <div className="empty-div" />
        <div>
          {numPages > 0 && (
            <span>
              {numPages}{" "}
              {numPages === 1 ? translate("page") : translate("pages").toLowerCase()}
            </span>
          )}
        </div>
        <div className="viewer-buttons">
          <IconButton onClick={zoomOut}>
            <MdZoomOut size={22} color={colors.gray500} />
          </IconButton>
          <IconButton onClick={resetZoom}>
            <TbZoomReset size={22} color={colors.gray500} />
          </IconButton>
          <IconButton onClick={zoomIn}>
            <MdZoomIn size={22} color={colors.gray500} />
          </IconButton>
        </div>
      </PdfViewerFooter>
    </div>
  );
}
